import { A } from '@solidjs/router';
import { createEffect, createSignal, onCleanup, Show } from 'solid-js';
import PublicLayout from '../PublicLayout';

export const POST_META = {
  slug: 'ghost-inventory',
  title:
    'The Ghost in the Machine: Eliminating Phantom Stock and Dead Inventory',
  description:
    'Phantom stock and dead inventory are silent profit killers. Learn how cycle counting, lean reorder points, FIFO batch tracking, and real-time sync eliminate them for good.',
  date: 'May 6, 2026',
  dateIso: '2026-05-06',
  readTime: '9 min read',
  category: 'Inventory Control',
  heroImage: '/blog-ghost-inventory.jpg',
  ogImage: '/blog-ghost-inventory.jpg',
};

export default function GhostInventoryPost() {
  const [heroFailed, setHeroFailed] = createSignal(false);
  const [alertUiFailed, setAlertUiFailed] = createSignal(false);
  const [cycleCountFailed, setCycleCountFailed] = createSignal(false);

  createEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: POST_META.title,
      description: POST_META.description,
      datePublished: POST_META.dateIso,
      dateModified: POST_META.dateIso,
      image: `https://khoai.io${POST_META.ogImage}`,
      url: 'https://khoai.io/blogs/ghost-inventory',
      inLanguage: 'en',
      author: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
      publisher: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://khoai.io/og-welcome.jpg',
        },
      },
      isPartOf: {
        '@type': 'Blog',
        name: 'Khoai Blog',
        url: 'https://khoai.io/blogs',
      },
    };

    let script = document.getElementById(
      'ld-json-blog-ghost-inventory'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-blog-ghost-inventory';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    let bcScript = document.getElementById(
      'ld-json-blog-ghost-inventory-bc'
    ) as HTMLScriptElement | null;
    if (!bcScript) {
      bcScript = document.createElement('script');
      bcScript.type = 'application/ld+json';
      bcScript.id = 'ld-json-blog-ghost-inventory-bc';
      document.head.appendChild(bcScript);
    }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://khoai.io',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://khoai.io/blogs',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: schema.headline,
          item: schema.url,
        },
      ],
    });
  });

  onCleanup(() => {
    document.getElementById('ld-json-blog-ghost-inventory')?.remove();
    document.getElementById('ld-json-blog-ghost-inventory-bc')?.remove();
  });

  const h2 = 'mt-12 text-2xl font-extrabold text-gray-900 sm:text-3xl';
  const h3 = 'mt-8 text-xl font-bold text-gray-900';
  const p = 'mt-4 text-base leading-8 text-gray-600';
  const ul = 'mt-4 list-disc space-y-2 pl-6 text-base leading-7 text-gray-600';
  const imgFallback =
    'flex min-h-[320px] w-full items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400';

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav class="mb-8 flex items-center gap-2 text-sm text-gray-400">
          <A href="/blogs" class="hover:text-amber-600">
            Blog
          </A>
          <span>/</span>
          <span class="text-gray-600">Inventory Control</span>
        </nav>

        {/* Header */}
        <header>
          <span class="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-700">
            Inventory Control
          </span>
          <h1 class="mt-4 text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
            The Ghost in the Machine: Eliminating Phantom Stock and Dead
            Inventory
          </h1>
          <p class="mt-4 text-lg leading-relaxed text-gray-500">
            Phantom stock and dead inventory are silent profit killers. Learn
            how cycle counting, lean reorder points, FIFO batch tracking, and
            real-time sync eliminate them for good.
          </p>
          <div class="mt-5 flex items-center gap-4 text-sm text-gray-400">
            <span>May 6, 2026</span>
            <span>·</span>
            <span>9 min read</span>
          </div>
        </header>

        {/* Hero image — 1200×628 */}
        <div class="mt-10 overflow-hidden rounded-2xl">
          <Show
            when={!heroFailed()}
            fallback={
              <div class={imgFallback} style={{ 'min-height': '340px' }}>
                Customer looking disappointed at an Out of Stock notification,
                blurred warehouse in background
              </div>
            }
          >
            <img
              src="/blog-ghost-inventory.jpg"
              alt="Customer looking disappointed at an Out of Stock notification on their phone, with a blurred messy warehouse in the background"
              width={1200}
              height={628}
              class="w-full object-cover"
              onError={() => setHeroFailed(true)}
            />
          </Show>
        </div>

        {/* ── Body ── */}
        <div class="mt-12">
          {/* Hook */}
          <p class={p}>
            The email arrives at 9:14 on a Tuesday morning. The subject line
            reads: <em>"Update on your order #8841."</em> The body, three
            sentences:{' '}
            <em>
              "We&apos;re sorry, but one of the items in your order is no longer
              available. We have cancelled your order and issued a full refund.
              We apologize for the inconvenience."
            </em>
          </p>
          <p class={p}>
            The customer had placed that order two days earlier. The product
            page said "In Stock." They&apos;d already told their team the
            supplies were on the way. Now they&apos;re on your competitor&apos;s
            website.
          </p>
          <p class={p}>
            This is the moment{' '}
            <strong class="font-semibold text-gray-900">phantom stock</strong>{' '}
            turns from an operational embarrassment into a business problem. The
            inventory system said you had units. You didn&apos;t. And the ripple
            from that single mismatch — the lost order, the refund processing
            cost, the customer you may never win back — plays out silently,
            across dozens of SKUs, every single week in warehouses that rely on
            data they can&apos;t fully trust.
          </p>

          {/* Section 1 */}
          <h2 class={h2}>The Real Cost of a Stock-Out</h2>
          <p class={p}>
            Industry estimates put the average e-commerce cart abandonment
            triggered by an out-of-stock event at 3–4x the value of the single
            lost sale. You&apos;re not just losing today&apos;s transaction —
            you&apos;re losing{' '}
            <strong class="font-semibold text-gray-900">
              customer lifetime value
            </strong>
            . A B2B buyer who gets cancelled on once will test another supplier
            for the next order. If that supplier delivers, you may never see
            them again.
          </p>
          <p class={p}>
            The compounding damage doesn&apos;t stop there. Marketplaces like
            Shopee, Lazada, and Tiki penalize sellers for cancellation rates.
            High cancellation rates depress your ranking in search results,
            which depresses discovery, which depresses revenue. A phantom stock
            problem that starts in the warehouse ends in your marketing funnel.
          </p>
          <p class={p}>
            On the other side of the same coin is{' '}
            <strong class="font-semibold text-gray-900">dead stock</strong> —
            inventory that arrived, never sold, and is now occupying shelf space
            and warehouse square footage you&apos;re paying for. If a SKU
            hasn&apos;t moved in 180 days, it isn&apos;t inventory anymore.
            It&apos;s a loan to yourself at zero interest, secured by products
            you may eventually have to liquidate at a loss.
          </p>

          {/* Section 2 */}
          <h2 class={h2}>How Ghosts Are Born</h2>
          <p class={p}>
            Phantom stock doesn&apos;t materialize out of nowhere. It
            accumulates through predictable failure modes, most of which are
            invisible until a customer order exposes them:
          </p>
          <ul class={ul}>
            <li>
              <strong class="font-semibold text-gray-900">
                Multi-channel sales without real-time sync.
              </strong>{' '}
              A unit sells on your Shopee storefront. Your inventory system
              doesn&apos;t know — or finds out 30 minutes later through a batch
              update. In that window, the same unit gets allocated to a direct
              order. You now have one product and two committed customers.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Returns that don&apos;t make it back to stock.
              </strong>{' '}
              A return arrives, it&apos;s physically in the building, but it
              hasn&apos;t been inspected, restocked, or logged. The system still
              shows it as sold. The unit sits in a returns bin for two weeks and
              nobody knows it&apos;s available.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Damaged goods that aren&apos;t written off.
              </strong>{' '}
              Twelve units got wet in a delivery. Six were discarded. Someone
              forgot to record the adjustment. Your system still shows twelve
              sellable units.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Manual count errors compounding over time.
              </strong>{' '}
              Each small error is survivable. Six months of small errors produce
              a stock count that&apos;s structurally wrong — off by 5–15% on
              your highest-velocity SKUs.
            </li>
          </ul>
          <p class={p}>
            None of these causes require negligence. They require only a system
            that can&apos;t capture reality fast enough to stay synchronized
            with it.
          </p>

          {/* Section 3 */}
          <h2 class={h2}>The Safety Stock Trap</h2>
          <p class={p}>
            The standard response to phantom stock anxiety is to over-order. If
            you can&apos;t trust your numbers, you carry more buffer. This
            solves the stock-out problem by creating a dead stock problem. Both
            destroy profit. They just do it through different mechanisms.
          </p>
          <p class={p}>
            The leaner approach is calculating a{' '}
            <strong class="font-semibold text-gray-900">reorder point</strong>{' '}
            based on actual data rather than fear. The formula is simple:
          </p>
          <div class="mt-6 rounded-2xl border border-gray-200 bg-gray-50 px-7 py-6 font-mono text-sm text-gray-700">
            <p>
              <span class="font-semibold text-gray-900">Reorder Point</span> =
              (Average Daily Usage × Supplier Lead Time) + Safety Stock
            </p>
            <p class="mt-3 text-xs text-gray-400">
              Safety Stock = Z-score × σ(demand) × √(Lead Time)
              <br />
              For most SMBs: Safety Stock ≈ 1.65 × daily demand std. deviation ×
              √(lead time in days) — a 95% service level without a statistics
              degree.
            </p>
          </div>
          <p class={p}>
            The important word in that formula is <em>actual</em>. Average daily
            usage calculated from real transaction history, not intuition.
            Supplier lead time measured from purchase orders, not vendor
            promises. When your reorder point is grounded in clean data, you
            order exactly what the math requires — no more safety-buffer
            hoarding, no more panicked emergency restocks at double the unit
            cost.
          </p>

          {/* Stock alert UI image — 800×500 */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!alertUiFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Mobile low stock alert notification UI mockup
                </div>
              }
            >
              <img
                src="/blog-stock-alert-ui.jpg"
                alt="Clean mobile notification mockup showing a low stock alert: Product Name — 2 units remaining, with a Reorder Now button"
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setAlertUiFailed(true)}
              />
            </Show>
          </div>

          {/* Khoai callout */}
          <div class="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <p class="text-base leading-8 text-gray-700">
              <strong class="font-semibold text-gray-900">
                Khoai calculates your reorder velocity automatically.
              </strong>{' '}
              Rather than exporting transaction history into a spreadsheet and
              running the formula manually every month, Khoai surfaces live
              reorder status per SKU — flagging items approaching their reorder
              point before they breach it. The system knows your supplier lead
              times, tracks your rolling daily usage, and pushes an alert the
              moment the math says &ldquo;order now.&rdquo; You set the
              parameters once. The vigilance is continuous.
            </p>
          </div>

          {/* Section 4 */}
          <h2 class={h2}>
            The Lead-Time Gap: Inventory in Transit Is Not On Your Shelf
          </h2>
          <p class={p}>
            One of the most overlooked contributors to phantom stock is{' '}
            <strong class="font-semibold text-gray-900">
              in-transit inventory
            </strong>{' '}
            — units you&apos;ve ordered from a supplier and paid for, but which
            haven&apos;t arrived yet. Many inventory systems count this stock as
            &ldquo;available.&rdquo; It isn&apos;t. A customer order cannot be
            fulfilled from a container that&apos;s still two days from the port.
          </p>
          <p class={p}>
            The lead-time gap is the window between when a purchase order is
            raised and when the goods are physically checked into your warehouse
            and available to pick. During that window, your visible stock is a
            fiction. Anything you oversell against in-transit inventory is a
            cancellation waiting to happen.
          </p>
          <p class={p}>
            The fix is treating in-transit stock as a separate bucket in your
            inventory system — not zero, not available, but specifically
            &ldquo;incoming, due {'{'}date{'}'}. &rdquo; A platform that
            distinguishes between on-hand, committed, and incoming gives you a
            realistic picture of what you can actually promise a customer right
            now.
          </p>

          {/* Section 5 */}
          <h2 class={h2}>Cycle Counting: The End of the Annual Shutdown</h2>
          <p class={p}>
            Most warehouse operators run one inventory count per year. The
            warehouse stops. Every person on shift counts. It takes two days,
            costs a week of disruption, and produces a snapshot that&apos;s
            technically accurate on the day of the count and increasingly stale
            every day afterward.
          </p>
          <p class={p}>
            Cycle counting inverts this logic. Instead of one massive count per
            year, you count a small section every day. The warehouse never
            stops. The data is perpetually fresh. The annual shock is replaced
            by continuous calibration.
          </p>

          <h3 class={h3}>How to Structure a Cycle Count Program</h3>
          <ul class={ul}>
            <li>
              <strong class="font-semibold text-gray-900">
                Class A items (top 20% by revenue or velocity):
              </strong>{' '}
              Count weekly. These are your highest-risk phantom stock candidates
              because they move fastest and are most likely to be oversold.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Class B items (mid-tier):
              </strong>{' '}
              Count monthly. Enough movement to drift; frequent enough to catch
              errors before they cascade.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Class C items (slow movers):
              </strong>{' '}
              Count quarterly. Low velocity means low drift risk. Quarterly is
              sufficient to catch and correct errors before they produce dead
              stock surprises at year-end.
            </li>
          </ul>
          <p class={p}>
            A warehouse with 600 SKUs running a proper cycle count program
            touches every item in the building roughly 4–6 times per year —
            versus once with the traditional model. The difference in data
            confidence is not incremental. It&apos;s structural.
          </p>

          {/* Cycle counting image — 800×500 */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!cycleCountFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Worker calmly scanning a single shelf in a tidy warehouse
                </div>
              }
            >
              <img
                src="/blog-cycle-counting.jpg"
                alt="A warehouse worker calmly scanning a single shelf section in a tidy, well-organized warehouse, illustrating the ease of daily cycle counting"
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setCycleCountFailed(true)}
              />
            </Show>
          </div>

          {/* Section 6 */}
          <h2 class={h2}>The Expiration Race: FIFO and the Dead Stock Clock</h2>
          <p class={p}>
            For retail, food service, cosmetics, and FMCG — any category with a
            shelf life or sell-by date — phantom stock has a second dimension:
            age. The stock exists. It&apos;s counted correctly. But it&apos;s
            expired, or close enough that no customer will accept it.
          </p>
          <p class={p}>
            The standard countermeasure is{' '}
            <strong class="font-semibold text-gray-900">
              FIFO (First In, First Out)
            </strong>{' '}
            — the practice of always picking and shipping the oldest batch
            first. In theory, FIFO is simple. In practice, without batch
            tracking at the system level, it relies entirely on warehouse staff
            discipline to rotate product correctly on every single pick. That
            discipline erodes. New stock gets shelved in front of old. The
            oldest product gradually migrates to the back of the rack, then to
            the expiration bin, then to a write-off.
          </p>
          <p class={p}>
            Batch tracking closes this loop. When every unit is logged with a
            batch number and an expiry date at the point of receipt, your system
            can enforce FIFO automatically — sequencing pick lists so that the
            oldest batch is always the first one out. Staff don&apos;t have to
            remember. The picking order makes the right choice the default.
          </p>
          <p class={p}>
            Beyond expiry management, batch-level visibility is what turns a
            product recall from an operational catastrophe into a ten-minute
            task. You know exactly which orders received the affected batch. You
            contact those customers directly. You contain the exposure before it
            scales.
          </p>

          {/* Section 7 */}
          <h2 class={h2}>The Fix: Real-Time Sync Across Every Channel</h2>
          <p class={p}>
            Every problem described in this article shares a common root: a gap
            between physical reality and the number in your system. Phantom
            stock is that gap applied to availability. Dead stock is that gap
            applied to demand forecasting. Lead-time confusion is that gap
            applied to inbound logistics. FIFO failures are that gap applied to
            picking sequences.
          </p>
          <p class={p}>
            Spreadsheets cannot close these gaps — not because they&apos;re
            poorly designed, but because they&apos;re fundamentally passive. A
            spreadsheet doesn&apos;t know when a Shopify sale happens. It
            doesn&apos;t update when a return lands on the dock. It doesn&apos;t
            alert you when a batch is approaching expiry. It sits, static,
            waiting for a human to find the time to update it. And in a
            warehouse running 200 picks a day, that human is never not busy.
          </p>
          <p class={p}>
            Real-time sync means every transaction — every sale, receipt,
            return, adjustment, and transfer — flows immediately into a single
            authoritative record. There is no version of the truth that&apos;s
            five minutes behind. There is no &ldquo;let me check the
            sheet&rdquo; uncertainty. When a customer asks if something is in
            stock, the answer your team gives is the same answer the warehouse
            floor would give if you walked out and looked.
          </p>
          <p class={p}>
            That alignment — between what the system says and what the physical
            world contains — is what turns inventory management from a liability
            into a competitive asset. Businesses that trust their numbers move
            faster, carry less buffer, oversell less, and lose fewer customers
            to the kind of cancellation email that opened this article.
          </p>
          <p class="mt-4 text-base font-semibold leading-8 text-gray-800">
            Phantom stock isn&apos;t a warehouse problem. It&apos;s a data
            problem. And data problems have data solutions.
          </p>

          {/* CTA */}
          <div class="mt-14 rounded-2xl bg-amber-600 px-8 py-10 text-center text-white">
            <h2 class="text-2xl font-extrabold">
              Know your real stock count, right now.
            </h2>
            <p class="mt-3 text-base leading-7 text-amber-100">
              Khoai gives you a unified, real-time view of on-hand, committed,
              and incoming inventory across every channel and location — so the
              next stock-out email never has to be written.
            </p>
            <A
              href="/signup"
              class="mt-6 inline-block rounded-xl bg-white px-8 py-3 text-base font-bold text-amber-700 hover:bg-amber-50"
            >
              Start free with Khoai →
            </A>
          </div>
        </div>

        {/* Back to blog */}
        <div class="mt-16 border-t border-gray-200 pt-8">
          <A
            href="/blogs"
            class="text-sm font-semibold text-amber-600 hover:text-amber-700"
          >
            ← Back to all articles
          </A>
        </div>
      </article>
    </PublicLayout>
  );
}
